import type { Assistant } from "@inrev/assistant";
import type { SuretyAccountId } from "@inrev/common";
import { Option } from "@inrev/core";
import {
	type InfiniteData,
	type QueryClient,
	useInfiniteQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import { assistantConversationQueryKey, assistantConversationsQueryKey } from "../assistant.keys";

const flattenPages = (
	data: InfiniteData<Assistant.Dtos.Conversation.List.Response, Option<string>>,
) => data.pages.flatMap((page) => page.items);

/**
 * A conversation whose first answer still streams has nothing saved to name it by, so it takes the
 * name its send gave the conversation's cached copy.
 */
const nameUnsaved = (
	queryClient: QueryClient,
	item: Assistant.Dtos.Conversation.List.Item,
): Assistant.Dtos.Conversation.List.Item => {
	if (Option.isSome(item.title) || Option.isSome(item.fallbackTitle)) return item;
	const conversation = queryClient.getQueryData<Assistant.Dtos.Conversation.Get.Response | null>(
		assistantConversationQueryKey(item.id),
	);
	return {
		...item,
		fallbackTitle: Option.toNull(
			Option.flatMap(conversation, ({ fallbackTitle }) => fallbackTitle),
		),
	};
};

/**
 * The current admin's conversations on an account, newest activity first. Refreshed by invalidation:
 * after each turn, and when the open chat's generated title lands.
 */
export const useFetchAssistantConversations = (accountId: SuretyAccountId) => {
	const { get } = useRequest();
	const queryClient = useQueryClient();

	const query = useInfiniteQuery({
		queryKey: assistantConversationsQueryKey(accountId),
		meta: { suppressErrorModal: true },
		initialPageParam: undefined,
		queryFn: async ({ pageParam }: { pageParam: Option<string> }) => {
			const params = new URLSearchParams({ contextType: "account", contextId: accountId });
			if (Option.isSome(pageParam)) params.set("cursor", pageParam);
			const page = await get<Assistant.Dtos.Conversation.List.Response>(
				`/v2/admin/assistant/conversations?${params.toString()}`,
			);
			return { ...page, items: page.items.map((item) => nameUnsaved(queryClient, item)) };
		},
		getNextPageParam: (lastPage) => Option.toUndefined(lastPage.nextCursor),
		select: flattenPages,
	});

	return {
		conversations: query.data,
		conversationsError: query.error,
		conversationsIsFetching: query.isFetching,
		refetchConversations: query.refetch,
		hasMoreConversations: query.hasNextPage,
		loadingMoreConversations: query.isFetchingNextPage,
		loadMoreConversationsFailed: query.isFetchNextPageError,
		loadMoreConversations: query.fetchNextPage,
	};
};
